// eslint-disable-next-line wix-custom-rules/wixstores-file-names
import {Scope} from '@wix/app-settings-client';
import {I$WWrapper} from '@wix/native-components-infra/dist/es/src/types/types';
import {createSlotVeloAPIFactory} from '@wix/widget-plugins-ooi/velo';
import {CreateControllerFn} from '@wix/yoshi-flow-editor';
import _ from 'lodash';
import {getQueryParamsDiff} from '../services/queryParams/queryParamsUtils';
import {ExternalDataSourceConfig} from '../types/externalDataSourceConfig';
import {
  GalleryControllerExtraParams,
  IGalleryControllerConfig,
  VeloInputs,
  VeloInputsOnItemSelected,
} from '../types/galleryTypes';
import {GalleryStore} from './GalleryStore';
import {GalleryWidgetViewState} from '@wix/wixstores-client-storefront-sdk/dist/es/src/enums/galleryWidgetViewState';

type ResolveFn<T> = (value: T | PromiseLike<T>) => void;
export type ReportError = (e: string | Error, shouldRethrowException?: boolean) => any;

export const GalleryController = (
  extraParams: GalleryControllerExtraParams = {isUsingExternalDataSource: false}
): CreateControllerFn => {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  return (controllerParams) => {
    const {controllerConfig, flowAPI} = controllerParams;
    const {setProps, compId, type: componentId} = controllerConfig;
    const siteStore = controllerParams.appData.context.siteStore;

    const {isUsingExternalDataSource} = extraParams;

    let galleryStore: GalleryStore;
    let veloInputs: VeloInputs;
    let onReadyResolver: ResolveFn<void>;
    let galleryStoreIsCreatedResolver: ResolveFn<void>;
    let initializeCallback: Function;
    let previousQueryParams: {[key: string]: string};
    let config: IGalleryControllerConfig = _.clone(controllerConfig.config);
    let externalDataSourceConfig: ExternalDataSourceConfig;
    let refreshGalleryCalledBeforeInit: boolean = false;
    let storeInitialized: boolean = false;

    const waitUntilReady = new Promise<void>((resolve) => {
      onReadyResolver = resolve;
    });

    const galleryStoreIsCreated = new Promise<void>((resolve) => {
      galleryStoreIsCreatedResolver = resolve;
    });

    const slotAPIFactory = createSlotVeloAPIFactory(controllerConfig);

    /* istanbul ignore next: nothing to test  */
    const reportError: ReportError = (e: string | Error, shouldRethrowException = true) => {
      console.error(e);
      try {
        flowAPI.reportError(e);
      } catch (rethrowError) {
        if (!shouldRethrowException) {
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw e;
      }
    };

    const initializeVeloDataAfterStoreCreated = async (shouldInitializeStore: boolean) => {
      await waitUntilReady;
      galleryStore.setVeloInputs(veloInputs);

      if (shouldInitializeStore) {
        return galleryStore.setInitialState();
      }
      return Promise.resolve();
    };

    const initGalleryStore = async () => {
      if (isUsingExternalDataSource) {
        /*istanbul ignore next: cannot test*/
        if (!externalDataSourceConfig) {
          throw new Error(
            'External data source callbacks are not set, event though gallery is using external data source.'
          );
        }
        galleryStore.setExternalDataSourceConfig(externalDataSourceConfig);
      }

      await galleryStore
        .setInitialState()
        .then(() => {
          onReadyResolver();
          storeInitialized = true;
        })
        .catch(reportError);
    };

    const setVeloInputs = (newVeloInputs: VeloInputs) => {
      veloInputs = {
        ...veloInputs,
        ...newVeloInputs,
      };
    };

    const exports = () => {
      const onItemSelected = (
        callBack: VeloInputsOnItemSelected['callBack'],
        options: VeloInputsOnItemSelected['options'] = {preventNavigation: false, initializeStore: true}
      ) => {
        setVeloInputs({onItemSelected: {callBack, options}});
        if (galleryStore) {
          return initializeVeloDataAfterStoreCreated(options.initializeStore);
        }
      };

      return {
        initializeGallery: async () => {
          /*istanbul ignore next: cannot test*/
          if (galleryStore) {
            await initGalleryStore();
          } else {
            refreshGalleryCalledBeforeInit = true;
          }
        },
        clearGalleryQueryParams: () => {
          galleryStore?.clearGalleryQueryParams();
        },
        setExternalDataSourceConfig: async (newConfig: ExternalDataSourceConfig) => {
          await galleryStoreIsCreated;

          externalDataSourceConfig = newConfig;
          if (newConfig.onProductClicked) {
            void onItemSelected(newConfig.onProductClicked, {preventNavigation: false, initializeStore: false});
          }

          await initGalleryStore();
        },
        refreshGallery: () => {
          galleryStore.refreshGallery();
        },
        setCollection: async (collectionId: string): Promise<void> => {
          setVeloInputs({collectionId, productIds: undefined});
          if (galleryStore) {
            return initializeVeloDataAfterStoreCreated(true);
          }
        },
        setProducts: async (productIds: string[]) => {
          setVeloInputs({productIds, collectionId: undefined});
          if (galleryStore) {
            return initializeVeloDataAfterStoreCreated(true);
          }
        },
        onInitialize: (cb: Function) => {
          initializeCallback = cb;
        },
        onItemSelected,
        onProductsChanged: (cb: Function) => {
          setVeloInputs({onProductsChanged: cb});
          /* istanbul ignore if: hard to test */
          if (galleryStore) {
            return initializeVeloDataAfterStoreCreated(true);
          }
        },
      };
    };

    const updateConfig = (_$w: I$WWrapper, newConfig: IGalleryControllerConfig) => {
      config = _.clone(newConfig);
      void galleryStore.updateState(config, {APP: {}, COMPONENT: {}});
    };

    const updateAppSettings = (_$w: I$WWrapper, updates: {[key: string]: any}) => {
      /* istanbul ignore next */
      if (updates.scope === Scope.COMPONENT && updates.source === 'app-settings' && storeInitialized) {
        void galleryStore.updateState(config, {APP: undefined, COMPONENT: {}, appSettings: updates.payload});
      }
    };

    const reactToUrlChange = () => {
      if (_.isEqual(previousQueryParams, siteStore.location.query)) {
        return;
      }

      const newQueryParams = siteStore.location.query;
      const {isPageUpdated, isFiltersUpdated, isSortUpdated, isLanguageOrCurrencyUpdated} = getQueryParamsDiff({
        previousQueryParams,
        newQueryParams,
        filterQueryParamsKeys: galleryStore.queryParamsFilterKeys,
      });
      previousQueryParams = {...newQueryParams};

      if (isLanguageOrCurrencyUpdated) {
        void galleryStore.setInitialState();
        return;
      }

      if (isPageUpdated || isFiltersUpdated || isSortUpdated) {
        galleryStore.updateAppOnQueryParamsChange({isPageUpdated, isFiltersUpdated, isSortUpdated});
      }
    };

    const load = () => {
      reactToUrlChange();
    };

    const init = async () => {
      await siteStore.loadClientConfig();
      controllerParams.controllerConfig.wixCodeApi.location.onChange(() => load());

      await createGalleryStore();

      // initialization should not happen in this phase when using external data source
      if (!isUsingExternalDataSource || refreshGalleryCalledBeforeInit) {
        await initGalleryStore();
      }
    };

    const updateWidgetViewState = (viewState: GalleryWidgetViewState) => {
      void galleryStore.updateWidgetViewState(viewState);
    };

    const createGalleryStore = async () => {
      if (initializeCallback) {
        await Promise.resolve(initializeCallback()).catch(flowAPI.reportError);
      }

      previousQueryParams = {...siteStore.location.query};

      galleryStore = new GalleryStore(
        config,
        controllerParams.dangerousPublicDataOverride,
        setProps,
        siteStore,
        compId,
        componentId,
        flowAPI,
        reportError,
        slotAPIFactory,
        veloInputs
      );

      galleryStoreIsCreatedResolver();
    };

    return {
      async pageReady() {
        await init();
      },
      load,
      exports,
      updateConfig,
      updateAppSettings,
      updateWidgetViewState,
    };
  };
};
